// src/services/shelterValidation.ts
// ----------------------------------
// Pure helpers for the New DFV Service wizard + the DFV Service edit screen:
//   • generate the next Customer Code (Zephyr<STATE><NNN>),
//   • detect a duplicate code / name against existing services (incl. deactivated),
//   • list the mandatory fields that are still empty.
// No React / no DB — everything is passed in, so it unit-tests cleanly.

export type ShelterLike = {
  shelter_code?: string | null;
  shelter_name?: string | null;
  deactivated?: boolean | null;
};

// Mandatory Details fields (both screens). Customer Code + Taxable are handled
// separately: the code has its own uniqueness rules, Taxable is a Yes/No that is
// always set. Returns the human labels of the fields that are still blank.
const REQUIRED_FIELDS: Array<[string, string]> = [
  ["shelter_name", "Customer Name"],
  ["customer_type", "DFV Service Type/Customer Type"],
  ["state", "State"],
  ["sales_group", "Sales Order Group"],
  ["default_warehouse", "Default Warehouse"],
  ["date_onboarded", "Date Onboarded"],
];

export function missingRequiredShelterFields(
  form: Record<string, unknown> | null | undefined
): string[] {
  return REQUIRED_FIELDS.filter(([key]) => !String((form?.[key] ?? "") as string).trim()).map(
    ([, label]) => label
  );
}

// The next free Customer Code for a state: Zephyr<STATE><NNN> — the mixed-case
// "Zephyr" prefix BYTE-MATCHES Unleashed's CustomerCode format (e.g. "ZephyrNSW106");
// UPPERCASE state; 3-digit zero-padded number. The CustomerCode is immutable in
// Unleashed once created, so matching the casing here is what keeps the two-way sync
// keyed correctly. Scans existing codes CASE-INSENSITIVELY so a generated code
// continues the same per-state sequence as the imported "Zephyr…" codes (and any
// legacy all-caps ones) rather than restarting at 001 — keeping every generated code
// genuinely unique (incl. against deactivated services).
export function nextShelterCode(
  state: string | null | undefined,
  existing: ShelterLike[] | null | undefined
): string {
  const st = String(state || "")
    .trim()
    .toUpperCase();
  if (!st) return "";
  const re = new RegExp(`^zephyr${st}0*(\\d+)$`, "i");
  let max = 0;
  for (const s of existing || []) {
    const m = re.exec(String(s?.shelter_code || "").trim());
    if (m) {
      const n = Number.parseInt(m[1], 10);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `Zephyr${st}${String(max + 1).padStart(3, "0")}`;
}

// An existing service whose code matches (case-insensitive), incl. deactivated —
// or null. Used to block a duplicate code and point the admin at reactivation.
export function findCodeDuplicate(
  code: string | null | undefined,
  existing: ShelterLike[] | null | undefined
): ShelterLike | null {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (!c) return null;
  return (
    (existing || []).find(
      (s) =>
        String(s?.shelter_code || "")
          .trim()
          .toLowerCase() === c
    ) || null
  );
}

// An existing service whose NAME matches (case-insensitive, trimmed), incl.
// deactivated — or null. Used for the non-blocking duplicate-name warning.
export function findNameDuplicate(
  name: string | null | undefined,
  existing: ShelterLike[] | null | undefined
): ShelterLike | null {
  const n = String(name || "")
    .trim()
    .toLowerCase();
  if (!n) return null;
  return (
    (existing || []).find(
      (s) =>
        String(s?.shelter_name || "")
          .trim()
          .toLowerCase() === n
    ) || null
  );
}
